import CustomAvatar from '-/Custom/Avatar';
import { StyleSheet } from 'react-native-unistyles';
import Badge from '@/components/UI/Badge';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import Box from '../UI/Box';

export const PersonListItemBadgesFragment = graphql(
  /* GraphQL */ `
    fragment PersonListItemBadges on person {
      badge_assignments_aggregate(where: $badgeAssignmentsWhere) {
        aggregate {
          count
        }
      }
      badge_assignments(
        where: $badgeAssignmentsWhere
        limit: $badgeAssignmentsLimit
      ) {
        id
        badge_id
        person_id
        event_id
        preview_value
        badge {
          id
          color
          title
        }
      }
    }
  `,
);

export const PersonListItemAvatarFragment = graphql(
  /* GraphQL */ `
    fragment PersonListItemAvatar on person {
      color
      name
      avatar_id
    }
  `,
);

export const PersonListItemNameFragment = graphql(
  /* GraphQL */ `
    fragment PersonListItemName on person {
      name
    }
  `,
);

export const Badges = ({
  fragment,
}: {
  fragment: FragmentOf<typeof PersonListItemBadgesFragment>;
}) => {
  const person = readFragment(PersonListItemBadgesFragment, fragment);

  const totalBadgeAssignments =
    person.badge_assignments_aggregate?.aggregate?.count || 0;

  const numberOfDisplayedBadgeAssignments =
    person.badge_assignments?.length || 0;

  return (
    <Box style={style.badges}>
      {person.badge_assignments?.map((badgeAssignment) => {
        return (
          <Badge color={badgeAssignment.badge.color} key={badgeAssignment.id}>
            <Badge.Text>
              {badgeAssignment.preview_value || badgeAssignment.badge.title}
            </Badge.Text>
          </Badge>
        );
      })}
      {totalBadgeAssignments > numberOfDisplayedBadgeAssignments ? (
        <Badge>
          <Badge.Text>
            +{totalBadgeAssignments - numberOfDisplayedBadgeAssignments}
          </Badge.Text>
        </Badge>
      ) : null}
    </Box>
  );
};

export const Avatar = ({
  fragment,
}: {
  fragment: FragmentOf<typeof PersonListItemAvatarFragment>;
}) => {
  const person = readFragment(PersonListItemAvatarFragment, fragment);

  return (
    <CustomAvatar
      id={person.avatar_id ?? ''}
      size="md"
      color={person.color}
      name={person.name ?? ''}
    />
  );
};

export const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box flex space="lg" direction="horizontal" alignItems="center">
      {children}
    </Box>
  );
};

export const Content = ({ children }: { children: React.ReactNode }) => {
  return <Box flex>{children}</Box>;
};

export const Name = ({
  fragment,
}: {
  fragment: FragmentOf<typeof PersonListItemNameFragment>;
}) => {
  const person = readFragment(PersonListItemNameFragment, fragment);

  return (
    <Text numberOfLines={1} size="lg">
      {person.name}
    </Text>
  );
};

export const NameContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box direction="horizontal" space="sm" alignItems="center">
      {children}
    </Box>
  );
};

export const Annotation = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box direction="horizontal" space="sm" alignItems="center">
      <Box width={4} height={4} borderRadius={2} background="secondary" />
      <Text size="xs" variant="typographySecondary">
        {children}
      </Text>
    </Box>
  );
};

const style = StyleSheet.create((theme) => ({
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.space('xs'),
  },
}));
